import React, { memo, useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import _ from 'lodash'
import { Grid } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Fab from '@material-ui/core/Fab'
import classNames from 'classnames'

import styles from '../styles'
import { EJobActionTypes } from 'models'
import { SearchSelect } from './'
import { TeamsQuery, StoreValue } from 'store'

const useStyles = makeStyles(styles)

const Step3View: React.FunctionComponent<any> = props => {
	const { job, storeDispatch, handleBack, handleNext } = props
	const {
		team,
		teamLeader,
		jobCompany,
		searchForDefaultCompanyTeams,
		defaultCompanyTeams = [],
		error,
	} = job
	const { t } = useTranslation()
	const classes = useStyles()

	const chooseTeam = useCallback(
		team => {
			let teamMembers = []
			if (team && team.team) {
				teamMembers =
					team.team.members &&
					team.team.members.map((item: any) => {
						return {
							value: item.id,
							label: `${item.user.profile.firstName} ${item.user.profile.lastName}`,
						}
					})
			}
			storeDispatch({
				type: EJobActionTypes.setTeamMembers,
				teamMembers,
			})
			if (team && !team.team) {
				// reset teamLeader value

				storeDispatch({
					type: EJobActionTypes.setTeamLeader,
					teamLeader: undefined,
				})
			}
			storeDispatch({
				type: EJobActionTypes.setTeam,
				team,
			})
			storeDispatch({
				type: EJobActionTypes.setError,
				errors: {
					team: team ? false : true,
				},
			})
		},
		[storeDispatch],
	)
	const chooseTeamLeader = useCallback(
		teamLeader => {
			// setTeamMemberBoss(teamMember)
			storeDispatch({
				type: EJobActionTypes.setTeamLeader,
				teamLeader: teamLeader,
			})
		},
		[storeDispatch],
	)

	const { refetch } = useQuery(TeamsQuery, {
		skip: true,
	})

	const debouncedTeamsFetch = useCallback(
		_.debounce((searchTerm: any, callback: any) => {
			refetch({
				companySlug: jobCompany.slug,
				name: searchTerm,
			})
				.then((result: any) => {
					return callback(
						result.data.teams.map((item: any) => {
							let tempItem = {
								value: '',
								label: '',
								team: undefined,
							}
							tempItem.value = item.id
							tempItem.label = item.name
							tempItem.team = item
							return tempItem
						}),
					)
				})
				.catch((error: any) => callback(error, null))
		}, 500),
		[jobCompany],
	)

	const getTeamsOptions = useCallback(
		(input: any, callback: any) => {
			if (!input) {
				return Promise.resolve({ options: [] })
			}
			debouncedTeamsFetch(input, callback)
		},
		[debouncedTeamsFetch],
	)

	const handleNextStep = useCallback(() => {
		if (_.isEmpty(team)) {
			storeDispatch({
				type: EJobActionTypes.setError,
				errors: {
					team: true,
				},
			})
		} else {
			handleNext()
		}
	}, [handleNext, storeDispatch, team])

	useEffect(() => {
		if (jobCompany && searchForDefaultCompanyTeams) {
			refetch({
				companySlug: jobCompany.slug,
			})
				.then((result: any) => {
					const defaultTeamsData = result.data.teams.map(
						(item: any) => {
							let tempItem = {
								value: '',
								label: '',
								team: undefined,
							}
							tempItem.value = item.id
							tempItem.label = item.name
							tempItem.team = item
							return tempItem
						},
					)
					defaultTeamsData.unshift({
						value: '',
						label: t('Allocated to the whole company'),
						team: null,
					})
					storeDispatch({
						type: EJobActionTypes.setSearchForDefaultCompanyTeams,
						value: false,
						data: defaultTeamsData,
					})
				})
				.catch((error: any) => console.error(error))
		}
	}, [jobCompany, refetch, searchForDefaultCompanyTeams, storeDispatch, t])

	return (
		<div>
			<Grid container direction={'row'} spacing={2}>
				<Grid item xs={12} className={classes.finalStepTitle}>
					<span>{t('Finally, introduce the team!')}</span>
				</Grid>
				<Grid item xs={12} sm={6} className={classes.fieldContainer}>
					<div
						className={classNames(classes.labelTextField, {
							[classes.labelFieldError]: error.team,
						})}
					>
						{t('What team is this part of?')}
						{
							<span className={classes.requiredField}>
								{`(${t('Required')})`}
							</span>
						}
					</div>
					<SearchSelect
						placeholderText={t('Search among your teams ...')}
						handleChange={chooseTeam}
						loadOptions={getTeamsOptions}
						isMulti={false}
						singleValue={team}
						defaultOptions={defaultCompanyTeams}
					/>
				</Grid>
				<Grid item xs={12} sm={6} className={classes.fieldContainer}>
					<span className={classes.labelTextField}>
						{t('And who will be the boss?')}
					</span>
					<SearchSelect
						placeholderText={t('Search for a team member ...')}
						options={job.teamMembers}
						isMulti={false}
						value={!teamLeader ? [] : teamLeader}
						isAsyncSelect={false}
						handleChange={chooseTeamLeader}
					/>
				</Grid>
				<Grid item xs={12}>
					<div className={classes.btnsContainer}>
						<Button
							onClick={handleBack}
							// className={classes.button}
						>
							{t('Back')}
						</Button>
						{/* <Button onClick={handleSkip}>{t('Skip')}</Button> */}
						<Fab
							onClick={handleNextStep}
							className={classes.nextBtn}
							variant="extended"
							size="medium"
							color="primary"
							aria-label="Add"
						>
							{t('Review') || ''}
						</Fab>
					</div>
				</Grid>
			</Grid>
		</div>
	)
}

const Step3 = memo((props: any) => (
	<StoreValue keys={['job', 'storeDispatch']}>
		<Step3View {...props} />
	</StoreValue>
))

export default Step3
